import ProjectFileIcon from "@/components/projects/ProjectFileIcon";
import { ChevronDown, ChevronRight, Folder } from "lucide-react";
import { useState } from "react";
import type Project from "@/types/project";
import { ProjectFolder } from "@/types/project";

interface ExplorerProps {
    projects: Project[];
    explorerOpen: boolean;
    activeProject: string;
    onProjectSelect: (projectId: string) => void;
}

const FOLDERS: { id: Project["folder"]; label: string }[] = [
    { id: "favorites", label: "Favoris" },
    { id: "inProgress", label: "En cours" },
    { id: "archived", label: "Archive" },
];

export default function Explorer({
    projects,
    explorerOpen,
    activeProject,
    onProjectSelect,
}: ExplorerProps) {
    const [folders, setFolders] = useState<Record<ProjectFolder, boolean>>({
        projects: true,
        favorites: true,
        inProgress: true,
        archived: true,
    });

    const toggleFolder = (folderId: ProjectFolder) => {
        setFolders((current) => ({
            ...current,
            [folderId]: !current[folderId],
        }));
    };

    return explorerOpen && (
        <div className="w-64 border-r border-white/15 flex-col bg-[#050505] shrink-0 hidden lg:flex">
            <div className="px-4 py-2 flex justify-between items-center h-12 border-b border-white/15">
                <span className="text-[10px] font-mono uppercase tracking-widest text-on-surface-variant">Explorer</span>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar py-2">
                <div className="px-2">
                    <button
                        type="button"
                        onClick={() => toggleFolder("projects")}
                        className="w-full px-1 py-1 flex items-center gap-1.5 text-on-surface hover:bg-white/5 rounded cursor-pointer"
                    >
                        {folders.projects ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                        <Folder className="size-3.5 text-white" />
                        <span className="text-xs font-bold tracking-tight uppercase">Projets</span>
                    </button>

                    {folders.projects && (
                        <div className="ml-4 mt-1 space-y-0.5">
                            {FOLDERS.map(({ id, label }) => {
                                const folderProjects = projects.filter((p) => p.folder === id);
                                return (
                                    <div key={id}>
                                        <button
                                            type="button"
                                            onClick={() => toggleFolder(id)}
                                            className="w-full px-1 py-1 flex items-center gap-1.5 text-on-surface hover:bg-white/5 rounded cursor-pointer"
                                        >
                                            {folders[id] ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                                            <Folder className="size-3.5 text-white" />
                                            <span className="text-[11px] font-bold uppercase tracking-tight">{label}</span>
                                        </button>

                                        {folders[id] && folderProjects.length > 0 && (
                                            <div className="ml-5 flex flex-col">
                                                {folderProjects.map((project) => (
                                                    <button
                                                        key={project.id}
                                                        type="button"
                                                        onClick={() => onProjectSelect(project.id)}
                                                        className={`relative flex items-center gap-2 px-2 py-1 text-xs font-mono text-left rounded ${activeProject === project.id
                                                            ? "sidebar-active bg-primary/10 after:content-[''] after:absolute after:bottom-0 after:right-0 after:w-0.5 after:h-full after:bg-primary"
                                                            : "text-on-surface-variant hover:bg-white/5"
                                                            }`}
                                                    >
                                                        <ProjectFileIcon fileIcon={project.fileIcon} />
                                                        <span className="text-nowrap truncate">{project.fileName}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
